import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import isEqual from "lodash-es/isEqual";
import orderBy from "lodash-es/orderBy";
import { FormattedMessage, useIntl } from "react-intl";

import AnyColor from "@mapmycustomers/shared/types/AnyColor";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import { Button, TextField } from "@mapmycustomers/ui";

import ActivityTypeColorDropdown from "@app/scene/settings/component/ActivityTypes/component/ActivityTypeColorDropdown";
import {
  getActivityTypes,
  getIsActivityTypeCreating,
  getIsBulkActivityTypeUpdating,
} from "@app/store/activity";
import { bulkUpdateActivityType, createActivityType } from "@app/store/activity/actions";
import { RootState } from "@app/store/rootReducer";

import SettingPage from "../common/SettingPage";

import styles from "./ActivityTypesCommon.module.scss";
import ActiveList from "./component/ActiveList";
import InactiveList from "./component/InactiveList";
import updateActivityTypeActivity from "./util/updateActivityTypeActivity";

interface ActivityTypesProps {
  activityTypes: ActivityType[];
  bulkUpdateActivityType: typeof bulkUpdateActivityType.request;
  createActivityType: typeof createActivityType.request;
  isActivityTypeCreating: boolean;
  isBulkActivityTypeUpdating: boolean;
}

const ActivityTypes: React.FC<ActivityTypesProps> = ({
  activityTypes,
  bulkUpdateActivityType,
  createActivityType,
  isActivityTypeCreating,
  isBulkActivityTypeUpdating,
}) => {
  const intl = useIntl();
  const [newActivityTypeName, setNewActivityTypeName] = useState("");
  const [newActivityTypeColor, setNewActivityTypeColor] = useState<AnyColor | undefined>(undefined);
  const [activityTypesCurrent, setActivityTypesCurrent] = useState(activityTypes);

  const [activeTypes, inactiveTypes] = useMemo(() => {
    const activeResponse: ActivityType[] = [];
    const inactiveResponse: ActivityType[] = [];
    activityTypesCurrent.forEach((type) => {
      (type.active ? activeResponse : inactiveResponse).push(type);
    });
    return [
      orderBy(activeResponse, "displayOrder", ["asc"]),
      orderBy(inactiveResponse, "name", ["asc"]),
    ];
  }, [activityTypesCurrent]);

  const activityTypesModified = useMemo(() => {
    const getActivityTypeForComparison = ({
      active,
      checkInEnabled,
      color,
      displayOrder,
      name,
    }: ActivityType) => {
      return {
        active,
        checkInEnabled,
        color,
        displayOrder,
        name,
      };
    };

    return activityTypesCurrent.filter((activityTypeCurrent) => {
      return !isEqual(
        getActivityTypeForComparison(activityTypeCurrent),
        getActivityTypeForComparison(
          activityTypes.find((activityType) => activityTypeCurrent.id === activityType.id)!
        )
      );
    });
  }, [activityTypes, activityTypesCurrent]);

  const handleRename = useCallback(
    (activityType: ActivityType, newName: string) =>
      setActivityTypesCurrent((types) =>
        types.map((type) => (type.id === activityType.id ? { ...type, name: newName } : type))
      ),
    [setActivityTypesCurrent]
  );

  const handleCheckIn = useCallback(
    (activityType: ActivityType, checkInEnabled: boolean) =>
      setActivityTypesCurrent((types) =>
        types.map((type) => (type.id === activityType.id ? { ...type, checkInEnabled } : type))
      ),
    [setActivityTypesCurrent]
  );

  const handleChangeColor = useCallback(
    (activityType: ActivityType, color: AnyColor | undefined) =>
      setActivityTypesCurrent((types) =>
        types.map((type) =>
          type.id === activityType.id ? { ...type, color: color ?? null } : type
        )
      ),
    [setActivityTypesCurrent]
  );

  const handleActivityTypeCreate = useCallback(() => {
    createActivityType({
      activityType: {
        color: newActivityTypeColor,
        displayOrder: activeTypes.length,
        name: newActivityTypeName,
      },
      callback: () => {
        setNewActivityTypeName("");
        setNewActivityTypeColor(undefined);
      },
    });
  }, [activeTypes.length, createActivityType, newActivityTypeColor, newActivityTypeName]);

  const handleSaveChanges = useCallback(
    () => bulkUpdateActivityType(activityTypesModified),
    [activityTypesModified, bulkUpdateActivityType]
  );

  const handleUnDo = useCallback(() => {
    setActivityTypesCurrent(activityTypes);
  }, [activityTypes, setActivityTypesCurrent]);

  const handleActivate = useCallback((id, active) => {
    setActivityTypesCurrent((types) => updateActivityTypeActivity(types, id, active));
  }, []);

  useEffect(() => {
    setActivityTypesCurrent(activityTypes);
  }, [activityTypes, setActivityTypesCurrent]);

  return (
    <SettingPage
      changesSaving={isBulkActivityTypeUpdating}
      hasChanges={activityTypesModified.length > 0}
      onSaveChanges={handleSaveChanges}
      onUnDo={handleUnDo}
      title={intl.formatMessage({
        id: "settings.activityTypes.title",
        defaultMessage: "Activity Types",
        description: "Title of settings activity types page",
      })}
    >
      <div className={styles.createFormContainer}>
        <TextField
          className={styles.nameField}
          label={intl.formatMessage({
            id: "settings.activityTypes.nameInput.label",
            defaultMessage: "Create new type",
            description: "Activity type name input label on settings",
          })}
          labelClassName={styles.inputLabel}
          labelPosition="top"
          onChange={setNewActivityTypeName}
          placeholder={intl.formatMessage({
            id: "settings.activityTypes.nameInput.placeholder",
            defaultMessage: "Name your activity type",
            description: "Activity type name input placeholder on settings",
          })}
          suffix={
            <ActivityTypeColorDropdown
              onChange={setNewActivityTypeColor}
              value={newActivityTypeColor}
            />
          }
          value={newActivityTypeName}
        />
        <Button
          disabled={!newActivityTypeName}
          loading={isActivityTypeCreating}
          onClick={handleActivityTypeCreate}
          size="large"
          type="primary"
        >
          <FormattedMessage
            id="settings.activityTypes.create"
            defaultMessage="Create"
            description="Create button title on create Activity Type"
          />
        </Button>
      </div>
      <div className={styles.activityTypeListWrapper}>
        <ActiveList
          activeTypes={activeTypes}
          inactiveTypes={inactiveTypes}
          onActivate={handleActivate}
          onChangeColor={handleChangeColor}
          onModifyActivityTypes={setActivityTypesCurrent}
          onRename={handleRename}
          onToggleCheckIn={handleCheckIn}
        />
        <InactiveList
          inactiveTypes={inactiveTypes}
          onActivate={handleActivate}
          onRename={handleRename}
        />
      </div>
    </SettingPage>
  );
};

const mapStateToProps = (state: RootState) => ({
  activityTypes: getActivityTypes(state),
  isActivityTypeCreating: getIsActivityTypeCreating(state),
  isBulkActivityTypeUpdating: getIsBulkActivityTypeUpdating(state),
});

const mapDispatchToProps = {
  bulkUpdateActivityType: bulkUpdateActivityType.request,
  createActivityType: createActivityType.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(ActivityTypes);
